import { useCallback, useMemo, useRef } from 'react';
import { nativeObjectsPrivateApiAdapter } from 'services/nativeObjectsPrivateApiAdapter';
import useChannel from './use-channel';
import { MESSAGE_CHANNEL_COMM_TYPE } from 'constants/index';
import nativeObjectConfigs from 'configs/nativeObjectConfigs';
import { CommonUtils } from 'utils';

const useMessageChannelAdapter = () => {
  const searchDebounceTimerRef = useRef(null);
  const { postMessage } = useChannel();

  const getAccessToParentAppContext = useCallback(
    async context => {
      const { resource } = context;
      return await postMessage({
        action: MESSAGE_CHANNEL_COMM_TYPE.GET_ACCESS,
        resource,
      });
    },
    [postMessage]
  );

  const updateNativeObject = useCallback(
    async context => {
      const { model, id, modelProperties } = context;
      return await postMessage({
        action: MESSAGE_CHANNEL_COMM_TYPE.UPDATE_NATIVE_OBJECT,
        model,
        id,
        modelProperties,
      });
    },
    [postMessage]
  );

  const getLanguageListForTranslations = useCallback(
    async context => {
      try {
        const { id } = context;
        const response = await postMessage({
          action: MESSAGE_CHANNEL_COMM_TYPE.GET_LANGUAGES,
          id,
        });
        const { data } = response?.response ?? { data: null };
        return data;
      } catch (error) {
        console.error(error);
      }
    },
    [postMessage]
  );

  const getHostDataForEntitiesSchemaPayload = useCallback(
    async parameters => {
      try {
        const { response, success, error } = await postMessage({
          action: MESSAGE_CHANNEL_COMM_TYPE.INJECT_DATA_INTO_ENTITIES_PAYLOAD,
          parameters,
        });
        if (!success) throw new Error(error);
        if (!response || !response.data) return null;
        return response.data;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    [postMessage]
  );

  const navigate = useCallback(
    context => {
      const { resource, parameters, ...options } = context;
      postMessage(
        {
          action: MESSAGE_CHANNEL_COMM_TYPE.NAVIGATE,
          resource,
          parameters,
          ...options,
        },
        false
      );
    },
    [postMessage]
  );

  const emitRouteChangeEvent = useCallback(
    (resourceOrContext, metaArg) => {
      const isContextObject = resourceOrContext && typeof resourceOrContext === 'object' && !Array.isArray(resourceOrContext);
      const resource = isContextObject ? resourceOrContext.resource : resourceOrContext;
      const meta = isContextObject ? resourceOrContext.meta : metaArg;
      const payload = { action: MESSAGE_CHANNEL_COMM_TYPE.ROUTE_CHANGE, resource };
      if (meta && (meta.object || meta.record)) {
        payload.meta = meta;
      }
      postMessage(payload, false);
    },
    [postMessage]
  );

  const onSchemaCRUD = useCallback(
    context => {
      const { action, resource, parameters } = context;
      postMessage(
        {
          action,
          resource,
          parameters,
        },
        false
      );
    },
    [postMessage]
  );

  const __getFieldDetailsFor = useCallback(
    async nativeObjIdentifier => {
      try {
        const response = await postMessage({
          action: MESSAGE_CHANNEL_COMM_TYPE.GET_NATIVE_OBJECT_FIELDS,
          model: nativeObjIdentifier,
        });
        const { data } = response?.response ?? { data: null };
        return CommonUtils.buildResponse(true, { meta: nativeObjIdentifier, response: data });
      } catch (error) {
        console.error(error);
        return CommonUtils.buildErrorResponse(error);
      }
    },
    [postMessage]
  );

  const getFieldDetailsFor = useCallback(
    async (nativeObjIdentifier, configType = 'default') => {
      const configs = nativeObjectConfigs[configType][nativeObjIdentifier];
      if (configs.messageChannelSupport) return await __getFieldDetailsFor(nativeObjIdentifier);
      else return nativeObjectsPrivateApiAdapter.getFieldDetailsFor(nativeObjIdentifier, configType);
    },
    [__getFieldDetailsFor]
  );

  const __getRecords = useCallback(
    async (nativeObjIdentifier, recordIds = [], configType = 'default') => {
      try {
        const configs = nativeObjectConfigs[configType][nativeObjIdentifier];
        const __recordIds = configs.hasBigIntegerIds ? recordIds.map(id => CommonUtils.convertValueToBigIntString(id)) : recordIds;
        const parameters = { [configs.bulkGetIdParamsKey]: __recordIds, ...configs.bulkGetParams };
        const response = await postMessage({
          action: MESSAGE_CHANNEL_COMM_TYPE.BULK_GET_NATIVE_OBJECT,
          model: nativeObjIdentifier,
          parameters,
        });
        const { data } = response?.response ?? { data: null };
        return CommonUtils.buildResponse(true, data);
      } catch (error) {
        console.error(error);
        return CommonUtils.buildErrorResponse(error);
      }
    },
    [postMessage]
  );

  const getRecords = useCallback(
    async (nativeObjIdentifier, recordIds = [], configType = 'default') => {
      const configs = nativeObjectConfigs[configType][nativeObjIdentifier];
      if (configs.messageChannelSupport) return await __getRecords(nativeObjIdentifier, recordIds, configType);
      else return nativeObjectsPrivateApiAdapter.getRecords(nativeObjIdentifier, recordIds, configType);
    },
    [__getRecords]
  );

  const __search = useCallback(
    async (nativeObjIdentifier, parameters) => {
      try {
        const response = await postMessage({
          action: MESSAGE_CHANNEL_COMM_TYPE.SEARCH_NATIVE_OBJECT,
          model: nativeObjIdentifier,
          parameters,
        });
        const { data } = response?.response ?? { data: null };
        return CommonUtils.buildResponse(true, data);
      } catch (error) {
        console.error(error);
        return CommonUtils.buildErrorResponse(error);
      }
    },
    [postMessage]
  );

  const searchNativeLookupChoices = useCallback(
    async (datasourceId, parameters = {}) => {
      try {
        const response = await postMessage({
          action: MESSAGE_CHANNEL_COMM_TYPE.SEARCH_NATIVE_LOOKUP_CHOICES,
          parameters: { datasourceId, ...parameters },
        });
        const { data } = response?.response ?? { data: null };
        return CommonUtils.buildResponse(true, data);
      } catch (error) {
        console.error(error);
        return CommonUtils.buildErrorResponse(error);
      }
    },
    [postMessage]
  );

  const resolveFieldValues = useCallback(
    async fieldResolutions => {
      try {
        const response = await postMessage({
          action: MESSAGE_CHANNEL_COMM_TYPE.RESOLVE_FIELD_VALUES,
          parameters: { field_resolutions: fieldResolutions },
        });
        const { data } = response?.response ?? { data: null };
        return CommonUtils.buildResponse(true, data);
      } catch (error) {
        console.error(error);
        return CommonUtils.buildErrorResponse(error);
      }
    },
    [postMessage]
  );

  const search = useCallback(
    async (nativeObjIdentifier, params = {}, configType = 'default') => {
      clearTimeout(searchDebounceTimerRef.current);
      const searchParams = { term: params.term, limit: params.page_size, ...params.subParameters };
      const configs = nativeObjectConfigs[configType][nativeObjIdentifier];
      if (configs.messageChannelSupport) {
        return new Promise((resolve, reject) => {
          searchDebounceTimerRef.current = setTimeout(async () => {
            const res = await __search(nativeObjIdentifier, searchParams);
            res.success ? resolve(res) : reject(res);
          }, 1500);
        });
      } else return nativeObjectsPrivateApiAdapter.search(nativeObjIdentifier, searchParams, configType);
    },
    [__search]
  );

  // Choice-source/native-dropdown data (data sources, their fields/sub-items, and choices) lives in the
  // host application, same as native object data above - the host makes the actual API call and returns
  // the raw response body over the channel under `data`, matching what the removed datasourceService's
  // REST calls used to resolve to, so datasourceMapper's parsing utils don't need to change.
  const getDatasources = useCallback(async () => {
    try {
      const response = await postMessage({ action: MESSAGE_CHANNEL_COMM_TYPE.GET_DATASOURCES });
      if (!response.success) {
        return CommonUtils.buildErrorResponse(response.error);
      }
      const { data } = response?.response ?? { data: null };
      return CommonUtils.buildResponse(true, data);
    } catch (error) {
      console.error(error);
      return CommonUtils.buildErrorResponse(error);
    }
  }, [postMessage]);

  const getNativeDropdownFields = useCallback(
    async parameters => {
      try {
        const response = await postMessage({ action: MESSAGE_CHANNEL_COMM_TYPE.GET_NATIVE_DROPDOWN_FIELDS, parameters });
        if (!response.success) {
          return CommonUtils.buildErrorResponse(response.error);
        }
        const { data } = response?.response ?? { data: null };
        return CommonUtils.buildResponse(true, data);
      } catch (error) {
        console.error(error);
        return CommonUtils.buildErrorResponse(error);
      }
    },
    [postMessage]
  );

  const getNativeDropdownSubItems = useCallback(
    async parameters => {
      try {
        const response = await postMessage({ action: MESSAGE_CHANNEL_COMM_TYPE.GET_NATIVE_DROPDOWN_SUBITEMS, parameters });
        if (!response.success) {
          return CommonUtils.buildErrorResponse(response.error);
        }
        const { data } = response?.response ?? { data: null };
        return CommonUtils.buildResponse(true, data);
      } catch (error) {
        console.error(error);
        return CommonUtils.buildErrorResponse(error);
      }
    },
    [postMessage]
  );

  const getNativeDropdownSubItemFields = useCallback(
    async parameters => {
      try {
        const response = await postMessage({ action: MESSAGE_CHANNEL_COMM_TYPE.GET_NATIVE_DROPDOWN_SUBITEM_FIELDS, parameters });
        if (!response.success) {
          return CommonUtils.buildErrorResponse(response.error);
        }
        const { data } = response?.response ?? { data: null };
        return CommonUtils.buildResponse(true, data);
      } catch (error) {
        console.error(error);
        return CommonUtils.buildErrorResponse(error);
      }
    },
    [postMessage]
  );

  const getNativeDropdownChoices = useCallback(
    async parameters => {
      try {
        const response = await postMessage({ action: MESSAGE_CHANNEL_COMM_TYPE.GET_NATIVE_DROPDOWN_CHOICES, parameters });
        if (!response.success) {
          return CommonUtils.buildErrorResponse(response.error);
        }
        const { data } = response?.response ?? { data: null };
        return CommonUtils.buildResponse(true, data);
      } catch (error) {
        console.error(error);
        return CommonUtils.buildErrorResponse(error);
      }
    },
    [postMessage]
  );

  return useMemo(() => {
    return {
      getAccessToParentAppContext,
      updateNativeObject,
      navigate,
      emitRouteChangeEvent,
      onSchemaCRUD,
      getFieldDetailsFor,
      getRecords,
      search,
      searchNativeLookupChoices,
      resolveFieldValues,
      getLanguageListForTranslations,
      getHostDataForEntitiesSchemaPayload,
      getDatasources,
      getNativeDropdownFields,
      getNativeDropdownSubItems,
      getNativeDropdownSubItemFields,
      getNativeDropdownChoices,
    };
  }, [
    getAccessToParentAppContext,
    updateNativeObject,
    navigate,
    emitRouteChangeEvent,
    onSchemaCRUD,
    getFieldDetailsFor,
    getRecords,
    search,
    searchNativeLookupChoices,
    resolveFieldValues,
    getLanguageListForTranslations,
    getHostDataForEntitiesSchemaPayload,
    getDatasources,
    getNativeDropdownFields,
    getNativeDropdownSubItems,
    getNativeDropdownSubItemFields,
    getNativeDropdownChoices,
  ]);
};

export default useMessageChannelAdapter;
